import { Flex, Button, DrawerFooter, Portal } from '@chakra-ui/react';
import { getVariantStyles } from '@/ui-library/components/molecules/KDrawer/KDrawer.styles';
import { ModalCloseButton } from '@/ui/ModalClosebutton';
import { useEffect, useRef, useState } from 'react';
import { CalendarInvites } from '../CalendarInvites/Container';
import { KText } from '@/ui-library/components';

export const SchedulerDetailsFooter = ({
  interviewPlan,
  previewPayload,
  previewDetails,
  interviewDataRef,
  navigationConfig,
  updateSharedData,
  registerForUpdates,
  currentInterviewIndex,
  updateInterviewDataCallback,
  currentTemplateData,
  updateTemplateModificationData,
  getSchedularDetailsModel,
}) => {
  const styles = getVariantStyles('default');
  const [isInviteEdit, setIsInviteEdit] = useState(false);
  const [calculatedCalendarHeight, setCalculatedCalendarHeight] = useState(300);
  const [alertBannerDeduction, setAlertBannerDeduction] = useState(0); // State for banner height deduction

  const ALERT_BANNER_SELECTOR = '.empty-variable-alert-container';
  const ASSUMED_ALERT_BANNER_HEIGHT = 120; // Approximate height of the banner in pixels

  useEffect(() => {
    const unregisterDatesSet = registerForUpdates(
      'editEmailInvites',
      (data) => {
        const { editEmailInvites } = data?.fullData ?? {};
        setIsInviteEdit((prev) =>
          prev !== editEmailInvites ? editEmailInvites : prev
        );
      }
    );

    return () => {
      unregisterDatesSet();
    };
  }, [registerForUpdates]);

  const calendarInvitesRef = useRef(null);
  const calendarInvitesWrapperRef = useRef<HTMLDivElement>(null); // Ref for the wrapper Flex

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    const wrapperElement = calendarInvitesWrapperRef.current;

    const checkForAlertBanner = () => {
      if (wrapperElement) {
        const bannerElement = wrapperElement.querySelector(
          ALERT_BANNER_SELECTOR
        );
        if (bannerElement) {
          setAlertBannerDeduction(ASSUMED_ALERT_BANNER_HEIGHT);
        } else {
          setAlertBannerDeduction(0);
        }
      }
    };

    if (isInviteEdit && wrapperElement) {
      resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          const height = entries[0].contentRect.height;
          setCalculatedCalendarHeight(Math.max(0, height));
          checkForAlertBanner(); // Check for banner on resize too
        }
      });
      resizeObserver.observe(wrapperElement);

      mutationObserver = new MutationObserver(() => {
        checkForAlertBanner();
      });
      mutationObserver.observe(wrapperElement, {
        childList: true,
        subtree: true,
      });

      const initialHeight = wrapperElement.clientHeight;
      setCalculatedCalendarHeight(Math.max(0, initialHeight));
      checkForAlertBanner(); // Initial check

      return () => {
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
      };
    } else {
      // Reset deduction if not in edit mode
      setAlertBannerDeduction(0);
    }
  }, [isInviteEdit]);

  const renderEmailInviteEdit = () => {
    const effectiveCalendarHeight = Math.max(
      0,
      calculatedCalendarHeight - alertBannerDeduction
    );

    const handleInviteEditClose = () => {
      updateSharedData({
        editEmailInvites: false,
      });
      setIsInviteEdit(false);
    };

    return (
      <>
        <Flex
          left="0"
          top="60px"
          zIndex="10"
          width="100%"
          position="absolute"
          borderTop="1px solid"
          flexDirection="column"
          borderColor="border-color"
          height="calc(100% - 50px)"
          backgroundColor="base-white"
        >
          <Flex
            ref={calendarInvitesWrapperRef}
            flexGrow={1}
            overflowY="auto"
            flexDirection="column"
          >
            <CalendarInvites
              containerHeight={effectiveCalendarHeight} // Pass the adjusted height
              scheduleType={'manual'}
              ref={calendarInvitesRef}
              interviewPlan={interviewPlan}
              previewPayload={previewPayload}
              previewDetails={previewDetails}
            />
          </Flex>

          <DrawerFooter
            {...styles.footer}
            flexShrink={0}
            padding="15px 20px"
            borderTop="1px solid"
            borderColor="border-color"
          >
            <Flex gap="10px" width="100%" justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={handleInviteEditClose}
                data-test-id="secondary-cta-button"
              >
                Back
              </Button>

              <Button
                textAlign="center"
                variant="primary"
                onClick={async () => {
                  const { candidate_comm_template, reviewer_comm_template } =
                    interviewDataRef.current ?? {};

                  let emailInviteData = {
                    hasValidData: false,
                    payload: {
                      candidate_description: candidate_comm_template?.body,
                      interviewer_description: reviewer_comm_template?.body,
                    },
                  };

                  if (
                    calendarInvitesRef.current &&
                    calendarInvitesRef.current.getModel
                  ) {
                    emailInviteData =
                      await calendarInvitesRef.current.getModel();
                  }

                  if (emailInviteData.hasValidData) {
                    // Detect modifications by comparing with stored original templates from bulk state
                    const candidateOriginal =
                      currentTemplateData?.candidateOriginalTemplate;
                    const reviewerOriginal =
                      currentTemplateData?.reviewerOriginalTemplate;

                    const candidateChanged =
                      candidateOriginal &&
                      candidateOriginal.body !==
                        emailInviteData?.payload?.candidate_description;
                    const reviewerChanged =
                      reviewerOriginal &&
                      reviewerOriginal.body !==
                        emailInviteData?.payload?.interviewer_description;

                    // Set modification flags in bulk-level state
                    const bulkUpdateData = {};
                    if (candidateChanged) {
                      bulkUpdateData['candidateTemplateModified'] = true;
                    }
                    if (reviewerChanged) {
                      bulkUpdateData['reviewerTemplateModified'] = true;
                    }

                    // Update bulk-level modification flags
                    if (
                      Object.keys(bulkUpdateData).length > 0 &&
                      updateTemplateModificationData
                    ) {
                      updateTemplateModificationData(bulkUpdateData);
                    }

                    // Update shared data for edit mode
                    updateSharedData({ editEmailInvites: false });

                    updateInterviewDataCallback({
                      data: {
                        ...interviewDataRef.current,
                        candidate_comm_template: {
                          ...interviewDataRef.current.candidate_comm_template,
                          body: emailInviteData?.payload?.candidate_description,
                        },
                        reviewer_comm_template: {
                          ...interviewDataRef.current.reviewer_comm_template,
                          body: emailInviteData?.payload
                            ?.interviewer_description,
                        },
                      },
                      currentIndex: currentInterviewIndex,
                    });
                    setIsInviteEdit(false);
                  }
                }}
                data-test-id="primary-cta-button"
              >
                Save
              </Button>
            </Flex>
          </DrawerFooter>
        </Flex>
        <Portal>
          <ModalCloseButton onClose={handleInviteEditClose} />
        </Portal>
      </>
    );
  };

  return (
    <>
      {isInviteEdit && <>{renderEmailInviteEdit()}</>}
      <DrawerFooter
        {...styles.footer}
        flexGrow={0}
        flexShrink={0}
        padding="15px 20px"
      >
        <Flex gap="10px">
          <Button
            variant="secondary"
            onClick={async () => {
              if (isInviteEdit) {
                updateSharedData({
                  editEmailInvites: false,
                });
              } else if (updateInterviewDataCallback) {
                if (navigationConfig?.isFirstStep) {
                  navigationConfig.goBack();
                } else {
                  // Get form data before navigation
                  const formData = await getSchedularDetailsModel();

                  updateInterviewDataCallback({
                    data: {
                      ...interviewDataRef.current,
                      ats_office: formData?.payload?.ats_office,
                      ats_office_id: formData?.payload?.ats_office_id,
                    },
                    currentIndex: currentInterviewIndex,
                    newIndex: currentInterviewIndex - 1,
                  });
                }
              }
            }}
            data-test-id="secondary-cta-button"
          >
            Back
          </Button>

          {/* Only show "Schedule all interviews now" on last step for manual scheduling (no recommendations) */}
          {(!navigationConfig?.isManualScheduling ||
            navigationConfig?.isLastStep) && (
            <Button
              variant="tertiary"
              onClick={async () => {
                if (updateInterviewDataCallback) {
                  // Validate current form before proceeding with schedule all
                  const formData = await getSchedularDetailsModel();

                  // If validation failed, getSchedularDetailsModel returns undefined and stops execution
                  if (!formData) {
                    return;
                  }

                  updateInterviewDataCallback({
                    data: {
                      ...interviewDataRef.current,
                      ats_office: formData?.payload?.ats_office,
                      ats_office_id: formData?.payload?.ats_office_id,
                    },
                    currentIndex: currentInterviewIndex,
                    isScheduleAll: true,
                  });
                }
              }}
              data-test-id="primary-cta-button"
              loadingText="Scheduling interviews"
              isLoading={navigationConfig?.isSubmitting}
              disabled={navigationConfig?.isSubmitting}
            >
              Schedule all interviews now
            </Button>
          )}

          {navigationConfig?.nextStepLabel && (
            <Button
              minW="170px"
              maxW="250px"
              variant="primary"
              onClick={async () => {
                if (updateInterviewDataCallback) {
                  // Get form data before navigation
                  const formData = await getSchedularDetailsModel();

                  updateInterviewDataCallback({
                    data: {
                      ...interviewDataRef.current,
                      ats_office: formData?.payload?.ats_office,
                      ats_office_id: formData?.payload?.ats_office_id,
                    },
                    currentIndex: currentInterviewIndex,
                    newIndex: currentInterviewIndex + 1,
                  });
                }
              }}
              data-test-id="primary-cta-button"
            >
              <KText isTruncated textAlign="center" color="base-white">
                Next: {navigationConfig?.nextStepLabel}
              </KText>
            </Button>
          )}
        </Flex>
      </DrawerFooter>
    </>
  );
};
